import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { MedicationMetadata } from "models/order-management/medicationMetadata";
import { Order, OrderComponent } from "models/order-management/models";
import {
  isOtherDose,
  OtherDose,
  ScriptSureDrugDoseResponse,
} from "models/scriptSure/scriptsureDrugDoseResponse";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "medication-strength-selector-scriptsure",
  templateUrl: "medication-strength-selector-scriptsure.html",
})
export class MedicationStrengthSelectorScripsureComponent
  implements OrderComponentInterface
{
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    const nameChanged =
      this._orderFormState.routedMedDescription !=
      orderFormState.routedMedDescription;
    const compoundToggled =
      this._orderFormState.isCompound != orderFormState.isCompound;
    this._orderFormState = orderFormState;

    if (nameChanged || compoundToggled) {
      // Changing the medication clears the strength dropdown
      this.clearStrengthDropdownOnMedicationChange();

      if (this._orderFormState.medRouteId && !this.isCompound) {
        // Update available doses based on name
        this.getMedicationDoses();
      }
    }
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  _orderFormState: OrderFormState = {};
  strengthOptions: (ScriptSureDrugDoseResponse | OtherDose)[] | null = null;
  selectedStrengthOption: string;
  prefilledDosageStrength: string;
  dosageDosage: string = "";
  dosageFrequency: string = "";
  dosageShowError: boolean = false;
  frequencyShowError: boolean = false;
  prevIsCompound: boolean = true;
  textFieldValue: string = "";

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    public medicationDataService: MedicationDataService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    if (
      this._orderFormState.formType === "amendment" ||
      this._orderFormState.formType === "convertToOrder"
    ) {
      this.setAmendedValue();
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get dosageRequired() {
    return this.orderComponent.properties.find(
      (component) => component.name == "dosageDosage"
    ).required;
  }

  get frequencyRequired() {
    return this.orderComponent.properties.find(
      (component) => component.name == "dosageFrequency"
    ).required;
  }

  get strengthDropdownDisabled() {
    return !this.strengthOptions;
  }

  get isCompound() {
    return this.orderCreateRequest.isCompound;
  }

  getMedicationDoses() {
    this.strengthOptions = null;
    this.medicationDataService
      .getMedicationDoses(this._orderFormState.medRouteId)
      .subscribe(
        (response: ScriptSureDrugDoseResponse[]) => {
          this.strengthOptions = response ? response : [];
          if (this._orderFormState.formType === "convertToOrder") {
            const result = this.strengthOptions.find(
              (option: ScriptSureDrugDoseResponse) =>
                `${option.MED_STRENGTH} ${option.MED_STRENGTH_UOM}` ===
                this.prefilledDosageStrength
            );
            const dosageStrength = result
              ? result.MED_MEDID_DESC
              : this.prefilledDosageStrength;
            if (!result && this.prefilledDosageStrength) {
              this.addOtherStrength(dosageStrength);
              this.prefilledDosageStrength = ""; // Only prefill once
            }
            this.selectedStrengthOption = dosageStrength;
            this.onStrengthOptionSelected(dosageStrength);
          }
        },
        (error) => {
          this.strengthOptions = [];
          console.error("Error when searching for medication options", error);
        }
      );
  }

  getStrength(strengthScriptSure: ScriptSureDrugDoseResponse | OtherDose) {
    if (isOtherDose(strengthScriptSure)) {
      return strengthScriptSure.otherDescription;
    }
    if (
      strengthScriptSure.MED_STRENGTH &&
      strengthScriptSure.MED_STRENGTH_UOM &&
      strengthScriptSure.MED_STRENGTH !== "" &&
      strengthScriptSure.MED_STRENGTH_UOM !== ""
    ) {
      return `${strengthScriptSure.MED_STRENGTH} ${strengthScriptSure.MED_STRENGTH_UOM}`;
    } else {
      return strengthScriptSure.MED_DOSAGE_FORM_DESC;
    }
  }

  getMedicationMetadata(
    strengthScriptSure: ScriptSureDrugDoseResponse | OtherDose
  ): MedicationMetadata | undefined {
    if (isOtherDose(strengthScriptSure)) {
      return;
    }
    return {
      GCN_SEQNO: strengthScriptSure.GCN_SEQNO,
      MED_ROUTED_MED_ID: strengthScriptSure.ROUTED_MED_ID,
      MED_ROUTED_MED_ID_DESC: strengthScriptSure.MED_ROUTED_MED_ID_DESC,
      MED_DOSAGE_FORM_DESC: strengthScriptSure.MED_DOSAGE_FORM_DESC,
      MED_STRENGTH: strengthScriptSure.MED_STRENGTH,
      MED_STRENGTH_UOM: strengthScriptSure.MED_STRENGTH_UOM,
    };
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    // Prefill strength value only for free text
    if (
      this.orderCreateRequest.isCompound &&
      this._orderFormState.formType === "convertToOrder"
    ) {
      this.textFieldValue = this.orderCreateRequest["dosageStrength"];
    }
    this.dosageDosage = this.orderCreateRequest["dosageDosage"];
    this.onDosageValueChange(this.dosageDosage);
    this.dosageFrequency = this.orderCreateRequest["dosageFrequency"];
    this.onFrequencyValueChange(this.dosageFrequency);
    this.prefilledDosageStrength = this.orderCreateRequest["dosageStrength"];
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/

  clearStrengthDropdownOnMedicationChange() {
    this.strengthOptions = null;
    this.selectedStrengthOption = "";

    this.orderCreateRequestEvent.emit({
      updates: { dosageStrength: "" },
    });
    this.checkIsValid();
  }

  onStrengthOptionSelected(event: string) {
    if (event === null || event === undefined || !this.strengthOptions) {
      this.checkIsValid();
      return;
    }
    const strengthScriptSure = this.strengthOptions.find(
      (strength) => strength.MED_MEDID_DESC === event
    );
    if (this.isAmendment()) {
      throw new Error("Amending strength is no longer supported");
    } else {
      const strength = this.getStrength(strengthScriptSure);
      const updates: any = {
        dosageStrength: strength,
        dosageForm: strengthScriptSure.MED_DOSAGE_FORM_DESC,
        ndc: strengthScriptSure.ndc,
        rxNormal: strengthScriptSure.rxNorm,
      };

      // Only emit nameDescription for non-compound medications
      // For compound medications, nameDescription is handled by medication-ingredients-scriptsure
      if (!this.isCompound) {
        updates.nameDescription = `${this._orderFormState.routedMedDescription} - ${strength}`;
      }

      this.orderCreateRequestEvent.emit({
        updates: {
          ...updates,
          medicationMetadata: this.getMedicationMetadata(strengthScriptSure),
        },
      });
    }

    this.checkIsValid();
  }

  onDosageValueChange(value: string) {
    if (value) {
      this.orderCreateRequestEvent.emit({
        updates: { dosageDosage: this.dosageDosage },
      });
    }
    this.dosageShowError = this.isBlankOrNA(this.dosageDosage);
    this.checkIsValid();
  }

  onFrequencyValueChange(value: string) {
    if (value) {
      this.orderCreateRequestEvent.emit({
        updates: { dosageFrequency: this.dosageFrequency },
      });
    }
    this.frequencyShowError = this.isBlankOrNA(this.dosageFrequency);
    this.checkIsValid();
  }

  onTextFieldValueChange(value: string) {
    const updates: any = {
      dosageStrength: value,
    };

    // Only emit nameDescription for non-compound medications
    // For compound medications, nameDescription is handled by medication-ingredients-scriptsure
    if (!this.isCompound) {
      updates.nameDescription = `${this._orderFormState.routedMedDescription} - ${value}`;
    }

    this.orderCreateRequestEvent.emit({
      updates: updates,
    });

    this.checkIsValid();
  }

  checkIsValid() {
    if (this.isCompound) {
      this.checkIsValidStrenghtFreeText();
      return;
    }
    if (
      (this.orderComponent.properties[0].required &&
        [this.dosageDosage, this.dosageFrequency].some(this.isBlankOrNA)) ||
      this.selectedStrengthOption === "" ||
      !this.selectedStrengthOption
    ) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  checkIsValidStrenghtFreeText() {
    if (
      (this.orderComponent.properties[0].required &&
        [this.dosageDosage, this.dosageFrequency].some(this.isBlankOrNA)) ||
      !this.textFieldValue ||
      this.textFieldValue === ""
    ) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  isBlankOrNA = (value: string): boolean =>
    value === "" || value.toUpperCase() === "N/A";

  isAmendment = (): boolean => this._orderFormState.formType === "amendment";

  addOtherStrength = (addTagText: string) => {
    this.strengthOptions = [
      ...this.strengthOptions,
      {
        MED_MEDID_DESC: addTagText,
        otherDescription: addTagText,
        MED_DOSAGE_FORM_DESC: undefined,
        ndc: undefined,
        rxNorm: undefined,
      },
    ];
  };

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/medication-strength-selector-scriptsure/medication-strength-selector-scriptsure.ts